import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../data/site";

const Badge = ({ status }) => {
  const cls =
    status === "paid" || status === "contacted"
      ? "border-gold/40 text-gold"
      : status === "failed" || status === "closed"
        ? "border-red-400/40 text-red-400"
        : "border-white/20 text-white/50";
  return <span className={`rounded-full border px-3 py-1 text-[10px] uppercase tracking-[0.15em] ${cls}`}>{status}</span>;
};

const Admin = () => {
  const [input, setInput] = useState("");
  const [ak, setAk] = useState("");
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("orders");
  const [orders, setOrders] = useState(null);
  const [enquiries, setEnquiries] = useState(null);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");

  const load = async (k) => {
    try {
      const h = { headers: { "x-admin-key": k } };
      const [o, e] = await Promise.all([
        axios.get(`${API}/admin/orders`, h),
        axios.get(`${API}/admin/enquiries`, h),
      ]);
      setOrders(o.data.orders);
      setEnquiries(e.data.enquiries);
      setAuthed(true);
      setAk(k);
      setError("");
      sessionStorage.setItem("sde_admin_key", k);
    } catch {
      setError("Invalid admin key.");
    }
  };

  const setStatus = async (id, status) => {
    try {
      await axios.post(`${API}/admin/enquiries/${id}/status`, { status }, { headers: { "x-admin-key": ak } });
      setEnquiries((es) => es.map((e) => (e.id === id ? { ...e, status } : e)));
      setNote(`Marked as ${status}.`);
    } catch {
      setNote("Could not update status.");
    }
  };

  const runFollowUps = async () => {
    try {
      const res = await axios.post(`${API}/admin/follow-ups/run`, {}, { headers: { "x-admin-key": ak } });
      setNote(res.data.sent ? `Sent ${res.data.sent} follow-up reminder${res.data.sent === 1 ? "" : "s"}.` : "No follow-ups are due yet.");
    } catch {
      setNote("Follow-up run failed.");
    }
  };

  useEffect(() => {
    const k = sessionStorage.getItem("sde_admin_key");
    if (k) load(k);
  }, []); // eslint-disable-line

  const TabBtn = ({ id, label, count }) => (
    <button
      onClick={() => setTab(id)}
      data-testid={`admin-tab-${id}`}
      className={`rounded-full border px-5 py-2.5 text-[11px] uppercase tracking-[0.18em] transition-all duration-300 ${
        tab === id ? "border-gold bg-gold/10 text-gold" : "border-white/12 text-white/55 hover:text-white"
      }`}
    >
      {label}{typeof count === "number" ? ` · ${count}` : ""}
    </button>
  );

  return (
    <div className="min-h-screen bg-ink px-5 py-16 text-white md:px-10" data-testid="admin-page">
      <div className="mx-auto max-w-6xl">
        <a href="/" data-testid="admin-back-link" className="text-[11px] uppercase tracking-[0.3em] text-gold">← Back to site</a>
        <h1 className="mt-6 font-serif text-5xl">Dashboard</h1>
        <p className="mt-2 text-mist">Paid orders and catering enquiries, newest first.</p>

        {!authed && (
          <form
            data-testid="admin-login-form"
            className="glass mt-10 flex max-w-md gap-3 rounded-2xl p-4"
            onSubmit={(e) => { e.preventDefault(); load(input); }}
          >
            <input
              data-testid="admin-key-input"
              type="password"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Admin key"
              className="w-full rounded-lg border border-white/12 bg-transparent px-4 py-3 text-sm focus:border-gold focus:outline-none"
            />
            <button data-testid="admin-login-button" className="btn-gold rounded-full px-6 text-[12px] font-semibold uppercase tracking-[0.15em]">
              View
            </button>
          </form>
        )}
        {error && <p data-testid="admin-error" className="mt-4 text-sm text-red-400">{error}</p>}

        {authed && (
          <>
            <div className="mt-8 flex flex-wrap items-center gap-3">
              <TabBtn id="orders" label="Orders" count={orders?.length} />
              <TabBtn id="enquiries" label="Enquiries" count={enquiries?.length} />
              {tab === "enquiries" && (
                <button onClick={runFollowUps} data-testid="run-followups-button" className="btn-ghost rounded-full px-5 py-2.5 text-[11px] uppercase tracking-[0.18em] text-white/70">
                  Send due follow-ups
                </button>
              )}
              {note && <span data-testid="admin-note" className="text-sm text-gold/90">{note}</span>}
            </div>

            {tab === "orders" && (
              <div className="mt-6 overflow-x-auto rounded-2xl border border-white/10" data-testid="admin-orders-table">
                <table className="w-full min-w-[720px] text-left text-sm">
                  <thead className="bg-surface text-[11px] uppercase tracking-[0.2em] text-gold">
                    <tr>{["Date", "Name", "Phone", "Items", "Total", "Payment"].map((h) => <th key={h} className="px-5 py-4 font-medium">{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {(orders || []).map((o) => (
                      <tr key={o.id} className="border-t border-white/8 text-white/75" data-testid={`admin-order-row-${o.id}`}>
                        <td className="whitespace-nowrap px-5 py-4">{new Date(o.created_at).toLocaleString()}</td>
                        <td className="px-5 py-4">{o.name}</td>
                        <td className="whitespace-nowrap px-5 py-4">{o.phone}</td>
                        <td className="max-w-[260px] truncate px-5 py-4" title={o.items.map((i) => `${i.qty}× ${i.name}`).join(", ")}>
                          {o.items.map((i) => `${i.qty}× ${i.name}`).join(", ")}
                        </td>
                        <td className="px-5 py-4 font-serif text-lg text-gold">${o.total.toFixed(2)}</td>
                        <td className="px-5 py-4"><Badge status={o.payment_status} /></td>
                      </tr>
                    ))}
                    {orders?.length === 0 && <tr><td colSpan={6} className="px-5 py-12 text-center text-mist">No orders yet.</td></tr>}
                  </tbody>
                </table>
              </div>
            )}

            {tab === "enquiries" && (
              <div className="mt-6 overflow-x-auto rounded-2xl border border-white/10" data-testid="admin-enquiries-table">
                <table className="w-full min-w-[900px] text-left text-sm">
                  <thead className="bg-surface text-[11px] uppercase tracking-[0.2em] text-gold">
                    <tr>{["Date", "Type", "Name", "Phone", "Email", "Event", "Guests", "Details", "Quote", "Status"].map((h) => <th key={h} className="px-5 py-4 font-medium">{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {(enquiries || []).map((e) => (
                      <tr key={e.id} className="border-t border-white/8 text-white/75" data-testid={`admin-row-${e.id}`}>
                        <td className="whitespace-nowrap px-5 py-4">{new Date(e.created_at).toLocaleString()}</td>
                        <td className="px-5 py-4"><Badge status={e.type} /></td>
                        <td className="px-5 py-4">{e.name}</td>
                        <td className="whitespace-nowrap px-5 py-4">{e.phone}</td>
                        <td className="px-5 py-4">{e.email || "—"}</td>
                        <td className="px-5 py-4">{e.event_type || "—"}{e.event_date ? ` · ${e.event_date}` : ""}</td>
                        <td className="px-5 py-4">{e.guests || "—"}</td>
                        <td className="max-w-[220px] truncate px-5 py-4">{e.message || e.items || "—"}</td>
                        <td className="px-5 py-4">
                          {e.type === "catering" ? (
                            <a href={`${API}/catering/quote/${e.id}.pdf`} data-testid={`quote-link-${e.id}`} className="text-[11px] uppercase tracking-[0.15em] text-gold hover:underline">PDF</a>
                          ) : "—"}
                        </td>
                        <td className="px-5 py-4">
                          {e.type === "catering" ? (
                            <div className="flex flex-col gap-2">
                              <Badge status={e.status || "new"} />
                              {e.follow_up_sent && <span className="text-[10px] uppercase tracking-[0.12em] text-white/35">reminded</span>}
                              {(e.status || "new") === "new" ? (
                                <button onClick={() => setStatus(e.id, "contacted")} data-testid={`mark-contacted-${e.id}`} className="text-left text-[11px] uppercase tracking-[0.12em] text-gold hover:underline">
                                  Mark contacted
                                </button>
                              ) : (
                                <button onClick={() => setStatus(e.id, "new")} data-testid={`reopen-${e.id}`} className="text-left text-[11px] uppercase tracking-[0.12em] text-white/45 hover:text-gold">
                                  Reopen
                                </button>
                              )}
                            </div>
                          ) : "—"}
                        </td>
                      </tr>
                    ))}
                    {enquiries?.length === 0 && <tr><td colSpan={10} className="px-5 py-12 text-center text-mist">No enquiries yet.</td></tr>}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Admin;
